// src/redux/companySlice.js
import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const initialState = {
  companies: [],
  company: null,
  loading: false,
  isSuccess: false,
  message: '',
  errors: [],
  favoris: {},

  // Lost items
  lostItems: [],
  lostLoading: false,
  lostError: '',

  // Jobs / Offres
  jobs: [],
  jobDetails: null,
  jobsLoading: false,
  jobsError: '',

  // Companies by country
  countryCompanies: [],
  countryLoading: false,
  countryError: '',

  // Business areas
  businessAreas: [],
  businessAreasLoading: false,
  businessAreasError: '',

  // Companies by business area
  businessAreaCompanies: [],
  businessAreaCompaniesLoading: false,
  businessAreaCompaniesError: ''
};

export const fetchCompanies = createAsyncThunk(
  'company/fetchCompanies',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/company/getAll');
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyByName = createAsyncThunk(
  'company/fetchCompanyByName',
  async ({ message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/getByName/${message}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyById = createAsyncThunk(
  'company/fetchCompanyById',
  async ({ companyid }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/company/${companyid}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addFavoris = createAsyncThunk(
  'company/addFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/user-favoris/add-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeFavoris = createAsyncThunk(
  'company/removeFavoris',
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/user-favoris/remove-favoris/${id}`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const getFavoris = createAsyncThunk(
  'company/getFavoris',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/user-favoris/get-liste-favoris`);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const COUNTRY_TEXT_VARIANTS = {
  'Tunisia':          ['tunis', 'tunisie', 'tunisia'],
  'Arabia Saoudite':  ['saudi', 'arabie saoudite', 'arabia saoudite', 'riyad', 'riyadh'],
  'Uae':              ['uae', 'emirates', 'emirats', 'dubai', 'abu dhabi'],
  'Rio':              ['rio', 'brasil', 'brazil'],
  'Rome':             ['rome', 'roma', 'italie', 'italy', 'italia'],
  'Greece':           ['greece', 'grece', 'athenes'],
  'Paris':            ['paris', 'france'],
  'Barcelone':        ['barcelone', 'barcelona', 'espagne', 'spain'],
  'Libia':            ['libia', 'libya', 'libye'],
  'Algeria':          ['algeria', 'algerie', 'alger'],
  'Egypt':            ['egypt', 'egypte', 'cairo', 'le caire'],
  'Maroc':            ['maroc', 'morocco', 'rabat', 'casablanca']
};

export const fetchCompaniesByCountry = createAsyncThunk(
  'company/fetchCompaniesByCountry',
  async ({ country, userLat, userLng }, { rejectWithValue, dispatch }) => {
    try {
      const variants = (COUNTRY_TEXT_VARIANTS[country] || [country?.toLowerCase()]).filter(Boolean);

      if (userLat != null && userLng != null) {
        // GPS available: nearby sorted by real user position, filtered by country text variants
        const response = await api.get(`/company/nearby?lat=${userLat}&lng=${userLng}`);
        const companies = Array.isArray(response.data) ? response.data : [];
        return companies.filter((c) => {
          const addr = (c.address || '').toLowerCase();
          const ctry = (c.country || '').toLowerCase();
          return variants.some((v) => addr.includes(v) || ctry.includes(v));
        });
      }

      // No GPS: getAll + client-side text filter, no distance ordering
      const { data } = await api.get('/company/getAll');
      const allCompanies = data?.data || (Array.isArray(data) ? data : []);
      return allCompanies.filter((company) => {
        const addr = (company.address || '').toLowerCase();
        const ctry = (company.country || '').toLowerCase();
        return variants.some((v) => addr.includes(v) || ctry.includes(v));
      });
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchBusinessAreas = createAsyncThunk(
  'company/fetchBusinessAreas',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get('/businessarea/all');
      return Array.isArray(data) ? data : data?.data || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompaniesByBusinessArea = createAsyncThunk(
  'company/fetchCompaniesByBusinessArea',
  async ({ businessAreaId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get('/company/getAll');
      const allCompanies = data?.data || (Array.isArray(data) ? data : []);
      return allCompanies.filter(
        (company) => company.business_area?._id === businessAreaId
      );
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchCompanyLostItems = createAsyncThunk(
  'company/fetchCompanyLostItems',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/company/lost-items/company/${companyId}`);
      return Array.isArray(data) ? data : data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== ✅ 1. Liste des offres ===================== 
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async (
    { companyId, status, sortBy = 'createdAt', order = 'desc' },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const query = new URLSearchParams();
      if (Array.isArray(status) && status.length) {
        status.forEach((s) => query.append('status', s));
      }
      if (sortBy) query.set('sortBy', sortBy);
      if (order) query.set('order', order);
      const qs = query.toString();

      const url = qs
        ? `/collaboration/offers/company/${companyId}?${qs}`
        : `/collaboration/offers/company/${companyId}`;

      const { data } = await api.get(url);
      return Array.isArray(data)
        ? data
        : (Array.isArray(data?.data) && data.data) || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
*/
export const fetchCompanyOffers = createAsyncThunk(
  'company/fetchCompanyOffers',
  async ({ companyId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.get(`/job-posts/company/${companyId}`);
      return Array.isArray(data) ? data : data?.data || data?.items || [];
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);
/* ===================== ✅ 2. Détails d'une offre ===================== */
export const fetchCompanyOfferDetails = createAsyncThunk(
  'company/fetchCompanyOfferDetails',
  async ({ companyId, offerId }, { rejectWithValue, dispatch }) => {
    try {
      const url = `/collaboration/offers/company/${companyId}/${offerId}`;
      const { data } = await api.get(url);
      return data;
    } catch (error) {
      if (error?.response?.status === 401) dispatch(logout());
      if (!error?.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

/* ===================== Slice ===================== */

const companiesSlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      /* --- Companies --- */
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanies.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.companies = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchCompanies.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Company By ID --- */
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompanyById.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        state.company = payload || null;
        state.loading = false;
      })
      .addCase(fetchCompanyById.rejected, (state, { payload }) => {
        state.isSuccess = false;
        state.errors = payload?.message;
        state.loading = false;
      })

      /* --- Favoris --- */
      .addCase(getFavoris.fulfilled, (state, { payload }) => {
        state.favoris = payload || null;
      })

      /* --- Lost Items --- */
      .addCase(fetchCompanyLostItems.pending, (state) => {
        state.lostLoading = true;
      })
      .addCase(fetchCompanyLostItems.fulfilled, (state, { payload }) => {
        state.lostLoading = false;
        state.lostItems = payload || [];
      })
      .addCase(fetchCompanyLostItems.rejected, (state, { payload }) => {
        state.lostLoading = false;
        state.lostError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- ✅ Jobs / Offers --- */
      .addCase(fetchCompanyOffers.pending, (state) => {
        state.jobsLoading = true;
        state.jobsError = '';
      })
      .addCase(fetchCompanyOffers.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobs = payload || [];
      })
      .addCase(fetchCompanyOffers.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement des offres';
      })

      /* --- Companies by country --- */
      .addCase(fetchCompaniesByCountry.pending, (state) => {
        state.countryLoading = true;
        state.countryError = '';
        state.countryCompanies = [];
      })
      .addCase(fetchCompaniesByCountry.fulfilled, (state, { payload }) => {
        state.countryLoading = false;
        state.countryCompanies = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchCompaniesByCountry.rejected, (state, { payload }) => {
        state.countryLoading = false;
        state.countryError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- Business areas --- */
      .addCase(fetchBusinessAreas.pending, (state) => {
        state.businessAreasLoading = true;
        state.businessAreasError = '';
      })
      .addCase(fetchBusinessAreas.fulfilled, (state, { payload }) => {
        state.businessAreasLoading = false;
        state.businessAreas = payload;
      })
      .addCase(fetchBusinessAreas.rejected, (state, { payload }) => {
        state.businessAreasLoading = false;
        state.businessAreasError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- Companies by business area --- */
      .addCase(fetchCompaniesByBusinessArea.pending, (state) => {
        state.businessAreaCompaniesLoading = true;
        state.businessAreaCompaniesError = '';
        state.businessAreaCompanies = [];
      })
      .addCase(fetchCompaniesByBusinessArea.fulfilled, (state, { payload }) => {
        state.businessAreaCompaniesLoading = false;
        state.businessAreaCompanies = payload;
      })
      .addCase(fetchCompaniesByBusinessArea.rejected, (state, { payload }) => {
        state.businessAreaCompaniesLoading = false;
        state.businessAreaCompaniesError = payload?.message || 'Erreur lors du chargement';
      })

      /* --- ✅ Job Offer Details --- */
      .addCase(fetchCompanyOfferDetails.pending, (state) => {
        state.jobsLoading = true;
        state.jobDetails = null;
      })
      .addCase(fetchCompanyOfferDetails.fulfilled, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobDetails = payload || null;
      })
      .addCase(fetchCompanyOfferDetails.rejected, (state, { payload }) => {
        state.jobsLoading = false;
        state.jobsError = payload?.message || 'Erreur lors du chargement du détail';
      });
  }
});

export default companiesSlice.reducer;
